import * as Popover from "@radix-ui/react-popover";
import { Flex } from "src/components/Flex.tsx";
import type { ClassAttributes, HTMLAttributes, PropsWithChildren } from "react";
import { useState } from "react";
import { Text } from "src/components/Text/Text.tsx";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { css } from "@emotion/react";
import { useTabsStore } from "src/components/Tabs/TabsProvider.tsx";
import { Observer } from "mobx-react-lite";

export const TabsDropdown = ({ children }: PropsWithChildren) => {
  const [open, setOpen] = useState(false);
  const store = useTabsStore();

  return (
    <Popover.Root onOpenChange={setOpen} open={open} modal={true}>
      <Popover.Trigger asChild>
        <Flex
          css={{
            marginLeft: 15,
            flexShrink: 0,
          }}
        >
          {children}
        </Flex>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content css={popoverContentStyles}>
          <Observer>
            {() => (
              <>
                {store.outOfViewTabs.map((tab) => (
                  <DropdownItem
                    onClick={() => {
                      setOpen(false);
                      tab.activate({
                        emitEvents: true,
                        updateState: !store.hasControlledTabId,
                      });
                    }}
                    key={tab.id}
                    onClose={() => tab.close()}
                  >
                    {tab.title}
                  </DropdownItem>
                ))}
              </>
            )}
          </Observer>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

type DropdownItemProps = PropsWithChildren<
  {
    onClose: () => void;
  } & HTMLAttributes<HTMLDivElement> &
    ClassAttributes<HTMLDivElement>
>;

const DropdownItem = ({ children, onClose, ...rest }: DropdownItemProps) => {
  const [isVisible, setIsVisible] = useState(false);
  return (
    <div
      css={itemContainerStyles}
      {...rest}
      onPointerEnter={() => setIsVisible(true)}
      onPointerLeave={() => setIsVisible(false)}
    >
      <Text css={itemTextStyles} variant="body2">
        {children}
      </Text>
      <div
        css={closeIconStyles({ isVisible: isVisible })}
        onMouseDown={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <i className={"icon-fl-15-close"} />
      </div>
    </div>
  );
};

const popoverContentStyles = (theme: ExactTheme) =>
  css({
    boxShadow: theme.shadowV2.default,
    zIndex: 22,
    position: "relative",
    minWidth: 108,
    borderRadius: 6,
    display: "flex",
    flexDirection: "column",
    background: "white",
  });

const itemContainerStyles = (theme: ExactTheme) =>
  css({
    position: "relative",
    height: "45px",
    paddingLeft: "15px",
    paddingRight: "40px",
    borderBottom: `1px solid ${theme.colorsV2.grey[100]}`,
    "&:last-child": {
      borderBottom: "none",
    },
    color: theme.colorsV2.grey[800],
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  });

const itemTextStyles = css({
  color: "inherit",
});

const closeIconStyles =
  ({ isVisible }: { isVisible: boolean }) =>
  (theme: ExactTheme) => css`
    position: absolute;
    top: 50%;
    transform-origin: center;
    transform: translateY(-50%);
    right: 7px;
    padding: 8px;
    width: 32px;
    flex-shrink: 0;
    height: 32px;
    color: ${theme.colorsV2.grey[600]};
    align-items: center;
    transition: 150ms;
    opacity: ${isVisible ? 1 : 0};

    :hover {
      color: ${theme.colors.red[500]};
    }
  `;
